import { TableCaptainBookingList } from 'components/Booking';
import { useDate } from 'components/hooks';
import { BookingClosed, BookingNotOpenedYet, ErrorScreen, MissingToken } from 'components/Info';
import { Footer, Navbar } from 'components/Navbar';
import { Spinner } from 'components/Spinner';
import moment, { Moment } from 'moment-timezone';
import queryString from 'query-string';
import React, { ReactNode, useEffect, useState } from 'react';
import Div100vh from 'react-div-100vh';
import { useSelector } from 'react-redux';
import { Dispatch, IRootState, useRematchDispatch } from 'rematch/store';

// query-string returns string | string[] | null - we only ever want a single trimmed value
const _getQueryParam = (value: string | (string | null)[] | null | undefined): string => {
	if (typeof value === 'string') {
		return value.trim();
	}
	return '';
};

interface IAuthGate {
	children: ReactNode;
}

export const AuthGate = (props: IAuthGate) => {
	const { children } = props;
	const { getUserTime, getEventDateFromFormat } = useDate();

	const parsed = queryString.parse(window.location.search);

	// ticket code can be passed in the URL (?ticketcode=... or ?code=...) for auto login
	const urlTicketCode = _getQueryParam(parsed.ticketcode) || _getQueryParam(parsed.code);

	const [tokenError, setTokenError] = useState<string | undefined>(undefined);
	const [ticketCodeError, setTicketCodeError] = useState<string | undefined>(undefined);
	const [isTokenResolveLoading, setIsTokenResolveLoading] = useState<boolean>(true);
	const [now, setNow] = useState<Moment>(moment());

	const token = useSelector((store: IRootState) => store.auth.token);
	const decryptedToken = useSelector((store: IRootState) => store.auth.decryptedToken);
	const storedTicketCode = useSelector((store: IRootState) => store.auth.ticketCode);
	const config = useSelector((store: IRootState) => store.data.config);
	const appError = useSelector((store: IRootState) => store.app.error);

	const resolveToken = useRematchDispatch((dispatch: Dispatch) => dispatch.auth.resolveToken);
	const checkTicketcode = useRematchDispatch((dispatch: Dispatch) => dispatch.auth.checkTicketcode);

	const startConfigSync = useRematchDispatch((dispatch: Dispatch) => dispatch.data.startConfigSync);
	const stopConfigSync = useRematchDispatch((dispatch: Dispatch) => dispatch.data.stopConfigSync);

	const startTeamsSync = useRematchDispatch((dispatch: Dispatch) => dispatch.data.startTeamsSync);
	const stopTeamsSync = useRematchDispatch((dispatch: Dispatch) => dispatch.data.stopTeamsSync);
	const startSpeakersSync = useRematchDispatch((dispatch: Dispatch) => dispatch.data.startSpeakersSync);
	const stopSpeakersSync = useRematchDispatch((dispatch: Dispatch) => dispatch.data.stopSpeakersSync);

	const startMyBookingsSync = useRematchDispatch((dispatch: Dispatch) => dispatch.booking.startMyBookingsSync);
	const stopMyBookingsSync = useRematchDispatch((dispatch: Dispatch) => dispatch.booking.stopMyBookingsSync);

	const startTeamBookingsSync = useRematchDispatch((dispatch: Dispatch) => dispatch.booking.startTeamBookingsSync);
	const stopTeamBookingsSync = useRematchDispatch((dispatch: Dispatch) => dispatch.booking.stopTeamBookingsSync);

	useEffect(() => {
		if (parsed.token && parsed.token !== token) {
			_resolveToken();
		} else if (urlTicketCode && (!token || urlTicketCode.toLowerCase() !== storedTicketCode?.toLowerCase())) {
			_resolveTicketCode();
		} else {
			setIsTokenResolveLoading(false);
			_startSyncs();
		}

		let interval: NodeJS.Timeout;

		interval = setInterval(() => {
			setNow(getUserTime());
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, []);

	useEffect(() => {
		if (decryptedToken) {
			_startSyncs();
		} else {
			_stopSyncs();
		}
	}, [decryptedToken]);

	const _startSyncs = () => {
		startConfigSync({});
		startTeamsSync({});
		startSpeakersSync({});
		startTeamBookingsSync({});
		if (decryptedToken?.ticketcode) {
			startMyBookingsSync({});
		}
	};

	const _stopSyncs = () => {
		stopConfigSync({});
		stopTeamsSync({});
		stopSpeakersSync({});
		stopMyBookingsSync({});
		stopTeamBookingsSync({});
	};

	const _resolveToken = async () => {
		const res = await resolveToken({ token: `${parsed.token}` });
		setIsTokenResolveLoading(false);
		if (res) {
			// setTokenError(res);
		}
	};

	const _resolveTicketCode = async () => {
		const res: any = await checkTicketcode({ ticketCode: urlTicketCode });
		setIsTokenResolveLoading(false);
		if (res) {
			// auto login failed -> fall back to the manual login mask, prefilled with the code from the URL
			const message = res?.error ?? res;
			setTicketCodeError(typeof message === 'string' ? message : 'Ticketcode not found');
		}
	};

	// Helper function to check if user can access booking
	const _canUserAccessBooking = (): boolean => {
		// Admin users can always access
		if (decryptedToken?.isAdmin) {
			return true;
		}

		// If booking is disabled, user cannot access
		if (!config?.bookingEnabled) {
			return false;
		}

		// If user's ticketcode is in slot exceptions, they can access
		if (decryptedToken?.ticketcode && config?.slotExceptions?.includes(decryptedToken.ticketcode)) {
			return true;
		}

		// If user has no slot defined, they cannot access (safety check)
		if (!decryptedToken?.slot || decryptedToken.slot <= 0) {
			return false;
		}

		// If activeBookingSlot is not defined, default to slot 1
		const activeSlot = config?.activeBookingSlot ?? 1;

		// If user's slot is not yet active, they cannot access
		if (decryptedToken.slot > activeSlot) {
			return false;
		}

		// Default: user can access
		return true;
	};

	// Helper function to check if booking is closed
	const _isBookingClosed = (): boolean => {
		return Boolean(config?.bookingEnd && getEventDateFromFormat(config.bookingEnd).isBefore(now));
	};

	// Helper function to get booking start time for user's slot
	const _getBookingStartTime = (): Moment => {
		// Use slot 1 as default if no slot is defined
		const slot = decryptedToken?.slot && decryptedToken.slot > 0 ? decryptedToken.slot : 1;
		const bookingStart = config?.bookingStart;

		if (!bookingStart) {
			throw new Error('Booking start time is not configured');
		}

		// Calculate offset: 10 minutes per slot (slot 1 = 0 minutes, slot 2 = 10 minutes, etc.)
		const offsetMinutes = 10 * (slot - 1);
		return getEventDateFromFormat(bookingStart, offsetMinutes);
	};

	const _renderContent = () => {
		// 1. Loading states - show spinners while data is being fetched
		if (isTokenResolveLoading) {
			return (
				<div className="w-full flex my-auto justify-center items-center">
					<Spinner />
				</div>
			);
		}

		if (tokenError) {
			return <div>{tokenError}</div>;
		}

		if (appError) {
			return <ErrorScreen message={appError} />;
		}

		if (!token) {
			return <MissingToken initialTicketCode={urlTicketCode} initialError={ticketCodeError} />;
		}

		if (!config) {
			return (
				<div className="h-full flex flex-col justify-center items-center">
					<Spinner />
				</div>
			);
		}

		// 2. Check if user can access booking (slot-based access control)
		if (!_canUserAccessBooking()) {
			try {
				return <BookingNotOpenedYet now={now} start={_getBookingStartTime()} slot={decryptedToken?.slot ?? 0} />;
			} catch (error) {
				// Fallback if booking start time calculation fails
				console.error('Error calculating booking start time:', error);
				return <BookingNotOpenedYet now={now} start={now} slot={decryptedToken?.slot ?? 0} />;
			}
		}

		// 3. Check if booking period has ended
		if (_isBookingClosed()) {
			return <BookingClosed />;
		}

		// 4. Render booking interface for authenticated users
		if (decryptedToken?.id) {
			return <TableCaptainBookingList id={decryptedToken.id} />;
		}

		// 5. Fallback to children (should rarely happen with proper token)
		return children;
	};

	return (
		<Div100vh>
			<div className="flex flex-col w-full h-full ">
				<Navbar />
				<div className="flex flex-1 bg-black overflow-y-auto">
					<div className="flex flex-col w-full flex-1 mx-auto" style={{ maxWidth: 1440 }}>
						{_renderContent()}
					</div>
				</div>
				<Footer />
			</div>
		</Div100vh>
	);
};
